import {
  type AppLanguage,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
} from "./config";

export function isSupportedLanguage(
  value: string | null | undefined,
): value is AppLanguage {
  return Boolean(value && SUPPORTED_LANGUAGES.includes(value as AppLanguage));
}

export function getStoredLanguage(): AppLanguage | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storedLanguage = window.localStorage
    .getItem(LANGUAGE_STORAGE_KEY)
    ?.toLowerCase();

  return isSupportedLanguage(storedLanguage) ? storedLanguage : null;
}

// The active language is encoded in the URL via the `/:lang` prefix, so the
// only fallbacks needed at first paint are the persisted localStorage choice
// and the default language.
export function detectPreferredLanguage(): AppLanguage {
  return getStoredLanguage() ?? DEFAULT_LANGUAGE;
}

export function stripLanguagePrefix(pathname: string): string {
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const [, firstSegment, ...restSegments] = normalizedPath.split("/");

  if (!isSupportedLanguage(firstSegment)) {
    return normalizedPath;
  }

  const remainingPath = restSegments.join("/");

  return remainingPath ? `/${remainingPath}` : "/";
}

export function buildLocalizedPath(
  language: AppLanguage,
  pathname: string = "/",
): string {
  const normalizedPath = stripLanguagePrefix(pathname);

  return normalizedPath === "/"
    ? `/${language}/`
    : `/${language}${normalizedPath}`;
}
